import { useState } from 'react';
import { ModelLanguage } from 'modules/EntityReference/CodeSnippets';
import { ApiMethodSignature } from 'types/swagger/clarifai-api';
import { useDeepCompareEffect } from 'react-use';
import { hasMethodSignatureName, MCP_TRANSPORT_NAME, OPENAI_TRANSPORT_NAME } from './modelPredict/pythonLlmMethodSignature';

function omitEnumKeys<T extends Record<string, string>, K extends keyof T>(enumObj: T, keysToOmit: readonly K[]): Omit<T, K> {
  const result = {} as Omit<T, K>;

  (Object.keys(enumObj) as Array<keyof T>).forEach((key) => {
    if (!keysToOmit.includes(key as K)) {
      // TypeScript needs a cast to Exclude<keyof T, K>
      (result as Record<Exclude<keyof T, K>, string>)[key as Exclude<keyof T, K>] = enumObj[key];
    }
  });

  return result;
}

const ReducedLanguages = omitEnumKeys(ModelLanguage, ['PythonOpenAI', 'PythonMCP', 'NodeJSOpenAI', 'NodeJSVercel']);

const DefaultLanguageOptions = Object.values(ReducedLanguages).map((lang) => ({ value: lang, name: lang }));

export const useLanguageOptionsForModels = ({
  modelMethodSignatures: methodSignatures,
  isInternalUser = false,
}: {
  modelMethodSignatures: ApiMethodSignature[] | undefined;
  isInternalUser?: boolean;
}) => {
  const [languageOptions, setLanguageOptions] = useState(DefaultLanguageOptions);
  const [selectedLanguage, setSelectedLanguage] = useState<ModelLanguage>(languageOptions[0].value);

  // HACK: Allow internal users to see all language options

  useDeepCompareEffect(() => {
    let newLanguageOptions = [...DefaultLanguageOptions];
    if (
      isInternalUser ||
      hasMethodSignatureName({
        name: OPENAI_TRANSPORT_NAME,
        methodSignatures,
      })
    ) {
      newLanguageOptions.push(
        {
          value: ModelLanguage.PythonOpenAI,
          name: ModelLanguage.PythonOpenAI,
        },
        {
          value: ModelLanguage.NodeJSOpenAI,
          name: ModelLanguage.NodeJSOpenAI,
        },
        {
          value: ModelLanguage.NodeJSVercel,
          name: ModelLanguage.NodeJSVercel,
        },
      );
    }
    if (
      hasMethodSignatureName({
        name: MCP_TRANSPORT_NAME,
        methodSignatures,
      })
    ) {
      newLanguageOptions = [
        {
          value: ModelLanguage.PythonMCP,
          name: ModelLanguage.PythonMCP,
        },
      ];
    }

    setLanguageOptions(newLanguageOptions.sort((a, b) => a.name.localeCompare(b.name)));
    if (
      hasMethodSignatureName({
        name: MCP_TRANSPORT_NAME,
        methodSignatures,
      })
    ) {
      setSelectedLanguage(ModelLanguage.PythonMCP);
    }
  }, [methodSignatures]);

  return { languageOptions, selectedLanguage, setSelectedLanguage };
};
